import { createLocaleContext, readLocaleSettings } from './context'
import { normalizeLocale } from './normalize'
import { getLocalePolicy } from './policy'
import { resolveLocale } from './resolver'
import { createLocaleStorage } from './storage'
import { getLocaleFromPath, readRedirectLocale } from './url'

export function readLocaleEnvironment() {
  return {
    location,
    navigator,
    storage: window.localStorage,
    isBot: window.IS_BOT || navigator.userAgent.includes('prerender'),
  }
}

function runtimeStorage(settings = readLocaleSettings(), environment = readLocaleEnvironment()) {
  const policy = getLocalePolicy(settings)
  return createLocaleStorage({
    storage: environment.storage,
    policy: policy.storage,
  })
}

export function readStoredLocaleRaw() {
  return runtimeStorage().readRaw()
}

export function getConsumerLocale({ consumer, profileLocale = null, getFallbackLocale }) {
  const policy = getLocalePolicy(readLocaleSettings())
  if (policy.consumers[consumer] === 'resolved') {
    return getFallbackLocale()
  }
  const selectedLocale = getAuthoritativeStoredLocale()
  if (selectedLocale) {
    return selectedLocale
  }
  return policy.consumers[consumer] === 'profile-first' && profileLocale ? profileLocale : getFallbackLocale()
}

export function shouldPersistI18nLocale() {
  return getLocalePolicy(readLocaleSettings()).persistence.i18n
}

export function getStoredLocale(availableLocales) {
  return runtimeStorage().read(availableLocales)
}

export function getAuthoritativeStoredLocale() {
  const settings = readLocaleSettings()
  const policy = getLocalePolicy(settings)
  return policy.storage.authoritative ? runtimeStorage(settings).readPrimary(settings.availableLocales) : null
}

export function setStoredLocale(value) {
  return runtimeStorage().write(value)
}

export function setStoredLocaleFromProfile(value, { preserveCurrent = false } = {}) {
  const settings = readLocaleSettings()
  const policy = getLocalePolicy(settings)
  if (policy.profile.preserveStored) {
    const adapter = runtimeStorage(settings)
    const selected = adapter.readPrimary(settings.profileLocales)
    if (selected) {
      return selected
    }
    if (preserveCurrent && !settings.webview) {
      return adapter.read()
    }
    const locale = normalizeLocale(value, settings.profileLocales)
    return locale ? adapter.write(locale) : adapter.read()
  }
  if (!policy.profile.overwrite) {
    return getStoredLocale()
  }
  const adapter = runtimeStorage(settings)
  const current = adapter.read(settings.profileLocales) || adapter.read()
  if (preserveCurrent) {
    return current
  }
  const locale = normalizeLocale(value, settings.profileLocales)
  return locale ? adapter.write(locale) : current
}

export function syncStoredLocaleFromRedirect(search, baseUrl, availableLocales) {
  const settings = readLocaleSettings()
  const policy = getLocalePolicy(settings)
  if (policy.storage.authoritative && runtimeStorage(settings).readPrimary(availableLocales)) {
    return null
  }
  const locale = readRedirectLocale(search, baseUrl, availableLocales, policy.persistence.mirror)
  if (locale) {
    setStoredLocale(locale)
  }
  return locale
}

export function resolveRuntimeLocale({
  stage,
  settings = readLocaleSettings({ stage }),
  urlLocale,
  fallbackOnly = false,
  region = null,
  environment = readLocaleEnvironment(),
}) {
  const policy = getLocalePolicy(settings)
  const adapter = runtimeStorage(settings, environment)
  const authoritative = policy.storage.authoritative && adapter.readPrimary(settings.availableLocales)
  if (authoritative) {
    if (policy.storage.fallbackKey && environment.storage.getItem(policy.storage.fallbackKey) !== authoritative) {
      adapter.write(authoritative)
    }
    return authoritative
  }
  let explicitLocale = urlLocale
  if (fallbackOnly) {
    explicitLocale = null
  } else if (explicitLocale === undefined) {
    explicitLocale = getLocaleFromPath(environment.location.pathname, settings.baseUrl, settings.availableLocales)
  }

  if (stage === 'bootstrap' && !fallbackOnly) {
    if (
      explicitLocale &&
      (policy.persistence.bootstrapPath === 'always' ||
        (policy.persistence.bootstrapPath === 'when-missing' && !adapter.read(settings.availableLocales)))
    ) {
      adapter.write(explicitLocale)
    }
    const mirrorLocale = readRedirectLocale(
      environment.location.search,
      settings.baseUrl,
      settings.availableLocales,
      policy.persistence.mirror,
    )
    if (mirrorLocale) {
      adapter.write(mirrorLocale)
    }
    explicitLocale ||= mirrorLocale
  }

  const context = createLocaleContext({ settings, policy, urlLocale: explicitLocale, region, environment })
  const priority = stage === 'bootstrap' ? policy.bootstrapPriority : policy.localePriority
  const result = resolveLocale({ priority }, context)
  if (explicitLocale && result.source === 'path' && policy.persistence.explicitPath) {
    const serialized = policy.storage.serialize(result.locale)
    if (adapter.readRaw() !== serialized) {
      adapter.write(result.locale)
    }
  }
  return result.locale
}
